import { useEffect, useState } from "react";
import { Sun, Menu, X, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { trackFormStart } from "../../lib/analytics";

const DEFAULT_NAV_LINKS = [
  { id: "jak-to-dziala", label: "Jak to działa" },
  { id: "quiz", label: "Quiz" },
  { id: "faq", label: "FAQ" },
];

export default function Header({
  ctaLabel = "Zrób bezpłatną wycenę",
  navLinks = DEFAULT_NAV_LINKS,
}) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToLeadForm = () => {
    trackFormStart("header");
    const el = document.getElementById("lead-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const input = document.getElementById("hero-phone");
    setTimeout(() => input?.focus(), 600);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-brand-border shadow-sm"
          : "bg-white/0 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 lg:h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2 group" data-testid="brand-logo">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-brand-yellow text-brand-yellowFg shadow-sm group-hover:rotate-12 transition-transform">
            <Sun strokeWidth={2.4} className="h-5 w-5" />
          </span>
          <span className="font-heading font-extrabold text-xl tracking-tight text-brand-ink">
            Heliora
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              className="text-sm font-medium text-brand-muted hover:text-brand-blue transition-colors"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            onClick={goToLeadForm}
            data-testid="header-cta-btn"
            className="hidden sm:inline-flex h-11 rounded-full bg-brand-yellow hover:bg-brand-yellowHover text-brand-yellowFg font-semibold px-5 shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-95 transition-all"
          >
            {ctaLabel}
            <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full text-brand-ink hover:bg-slate-100"
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-brand-border bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setMobileOpen(false)}
                className="px-3 py-3 rounded-xl text-base font-medium text-brand-ink hover:bg-brand-surface"
              >
                {l.label}
              </a>
            ))}
            <Button
              onClick={() => {
                setMobileOpen(false);
                goToLeadForm();
              }}
              data-testid="mobile-cta-btn"
              className="mt-2 h-12 rounded-full bg-brand-yellow hover:bg-brand-yellowHover text-brand-yellowFg font-semibold"
            >
              {ctaLabel}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
